import React, { useEffect, useState, useCallback } from 'react';
import '../pages/LandingPage.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import sortByAssessmentName, { sortNavigationCategories } from './sortingHelper';

import InstructionDE from "../asset/Anleitung_DE.pdf";
import InstructionITA from "../asset/Anleitung_ITA.pdf";
import InstructionENG from "../asset/Anleitung_ENG.pdf";

const config=require( '../config.js');
const translations = {
    english: {
        title: "Selection of Activities",
        description: "<ol><li><b>Select your sector</b>, then choose the specific activity your company performs to start the assessment.</li><li><b>Find finished and ongoing assessments</b> under the <b>Assessment tab</b> in the top menu.</li><li><b>Complete the Minimum Social Safeguards assessment</b> after assessing all desired activities.</li><li> <b>Generate an initial dashboard</b> with an overview of your <b>Taxonomy alignment</b> in the <b>Dashboard tab</b> once all assessments are done.</li><li><b>Create a report</b> from the dashboard, which will be saved in the <b>Report section</b> and can be <b>sent to an expert directly from there.</b></li></ol>",
        startButton: "Start Evaluation",
        category: "Category:",
        selectedCategory: "Manufacturing",
        instruction: "Download Usermanual",
    },
    german: {
        title: "Auswahl von Aktivitäten",
        description: "<ol><li>Wählen Sie Ihren Sektor und dann die spezifische Tätigkeit Ihres Unternehmens aus, um das Assessment zu starten.</li><li>Abgeschlossene und laufende Assessments finden Sie auf der Registerkarte „Assessments“ im oberen Menü.</li><li>Nachdem alle gewünschten Aktivitäten erfasst wurden, schließen Sie die Auswertung für die sozialen Mindeststandards ab.</li><li>Nach Abschluss aller Assessments, inklusive der Auswertung der sozialen Mindeststandards, lassen Sie sich über die Registerkarte Dashboard im oberen Menü, einen ersten Überblick über die Taxonomiekonformität Ihrer Aktivitäten geben.</li><li>Erstellen Sie aus dem Dashboard einen Bericht, der im Bereich „Reports“ gespeichert wird und dort an einen Experten zur Verifizierung gesendet werden kann.</li></ol>",
        startButton: "Assessment starten",
        category: "Kategorie:",
        selectedCategory: "Verarbeitendes Gewerbe",
        instruction: "Anleitung herunterladen",
    },
    italian: {
        title: "Selezione delle Attività",
        description: "<ol><li>Seleziona il tuo settore, quindi scegli l'attività specifica svolta dalla tua azienda per avviare la valutazione.</li><li>Puoi trovere le valutazioni completate e in corso nella scheda Assessments nel menu in alto.</li><li>Completa la valutazione dei Criteri Sociali Minimi di Salvaguardi dopo aver valutato tutte le attività desiderate.</li><li> Genera un dashboard iniziale con una panoramica dell'allineamento alla Tassonomia nella scheda Dashboard una volta completate tutte le valutazioni.</li><li>Crea un report dal dashboard, che verrà salvato nella sezione Reports e potrà essere inviato direttamente a un esperto.</li></ol>",
        startButton: "Inizia Valutazione",
        category: "Categoria:",
        selectedCategory: "Attività Manifatturiere",
        instruction: "Manuale",
    },
    czech: {
        title: "Výběr Aktivit",
        description: "<ol><li>Vyberte svůj sektor, poté zvolte konkrétní aktivitu, kterou vaše společnost vykonává, abyste zahájili hodnocení.</li><li>Najděte dokončená a probíhající hodnocení pod záložkou Hodnocení v horním menu.</li><li>Po hodnocení všech požadovaných aktivit dokončete hodnocení Minimálních sociálních záruk.</li><li>Vytvořte počáteční dashboard s přehledem vaší shody s taxonomií v záložce Dashboard, jakmile budou všechna hodnocení dokončena.</li><li>Vytvořte zprávu z dashboardu, která bude uložena v sekci Zprávy a může být přímo odtud zaslána odborníkovi.</li></ol>",
        startButton: "Zahájit Hodnocení",
        category: "Kategorie:",
        selectedCategory: "Zpracovatelský Průmysl",
        instruction: "Stáhnout uživatelskou příručku",
    }
};

const LandingPage = () => {
    const [assessments, setAssessments] = useState([]);
    const [currentLanguage, setCurrentLanguage] = useState(localStorage.getItem('language') || 'english');
    const [selectedCategory, setSelectedCategory] = useState(translations[currentLanguage].selectedCategory || 'Manufacturing');

    const navigate = useNavigate();

    const fetchAssessments = useCallback(async () => {
        try {
            const response = await fetch(config.env.API_BASE_URL+'/api/assessments', { 
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
            });
            if (response.ok) {
                const data = await response.json();
                setAssessments(data);
            } else {
                console.error('Failed to fetch assessments');
            }
        } catch (error) {
            console.error('Error fetching assessments:', error);
        }
    }, []);

    useEffect(() => {
        fetchAssessments();
    }, [fetchAssessments]);

    const startButton = (examName, examCategory, isMSS, assessmentId) => {
        navigate('/instructions', { state: { examName, examCategory, isMSS, assessmentId } });
    };

    const changeLanguage = (language) => {
        if (currentLanguage !== language) {
            localStorage.setItem('language', language);
            setCurrentLanguage(language);
        }
    };

    const lang = translations[currentLanguage];

    // Filter assessments based on the selected language
    const filteredAssessments = assessments.filter(
        assessment => assessment.language === currentLanguage
    );

    // Get unique categories from filtered assessments
    const uniqueCategories = [...new Set(filteredAssessments.map(assessment => assessment.examCategory))];
   
    // Further filter assessments based on selected category
    const categoryFilteredAssessments = filteredAssessments.filter(assessment => assessment.examCategory === selectedCategory);



    function getColorByCategory(category) {
        const normalizedCategory = category.trim().toLowerCase();
        
        switch (normalizedCategory) {
            case 'energie':
            case 'energy':
            case 'energia':
            case 'energetika': 
                return 'rgba(128, 118, 128, 0.7)'; // Purple
            
            case 'minimum social safeguards':
            case 'mss':
            case 'sozialer mindeststandard':
            case 'minimální sociální záruky':
            case 'criteri sociali minimi di salvaguardia':
            case 'sozialer mindeststandard':
            case 'mindestschutz':
                return 'rgba(184, 134, 11, 0.7)'; // Yellow
                
            case 'manufacturing':
            case 'verarbeitendes gewerbe/herstellung von waren':  
            case 'attività manifatturiere':
            case 'zpracovatelský průmysl':
                return 'rgba(84, 142, 162, 0.7)'; // Lighter blue
                
            case 'construction':
            case 'baugewerbe und immobilien':
            case 'produzione':
            case 'stavebnictví a činnosti v oblasti nemovitostí':
                return 'rgba(95, 135, 102, 0.7)'; // Green
                
            case 'professional, scientific and technical activities':
            case 'erbringung von freiberuflichen, wissenschaftlichen und technischen dienstleistungen':
            case 'attività professionali, scientifiche e tecniche':
            case 'odborné, vědecké a technické činnosti':
                return 'rgba(163, 93, 69, 0.7)'; // Dark orange
                
            default:
                return 'rgba(18, 118, 128, 0.7)'; // Darker blue
        }
    }

    function darkenColor(color) {
        const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
        if (!rgbaMatch) return color;
    
        let r = parseInt(rgbaMatch[1]);
        let g = parseInt(rgbaMatch[2]);
        let b = parseInt(rgbaMatch[3]);
    
        r = Math.max(0, r - 50);
        g = Math.max(0, g - 50);
        b = Math.max(0, b - 50);
    
        return `rgba(${r}, ${g}, ${b}, 1)`;
    }

    return (
        <div className='mt-5 d-flex justify-content-center align-items-center landing-page'>
            <section className='landing-main'>
                <h2>{lang.title}</h2>
                <h5 className='activity-text' dangerouslySetInnerHTML={{__html: lang.description}}>
                     
                </h5>
                <div className='activity-text'>
                    <a downlaod target="_blank" href={currentLanguage==='english'?InstructionENG: currentLanguage==='german'?InstructionDE : currentLanguage==='italian'?InstructionITA: InstructionENG}><h5>{translations[currentLanguage].instruction}</h5></a>
                </div>
                {/* Category buttons */}
                <div className='category-nav mt-5 text-start'>
                {uniqueCategories.sort(sortNavigationCategories).map(category => (
                    <button
                        key={category}
                        className={`nav-button ${selectedCategory === category ? 'active' : ''}`}
                        onClick={() => setSelectedCategory(category)}
                        style={{
                            backgroundColor: selectedCategory === category ? 
                                darkenColor(getColorByCategory(category)) : getColorByCategory(category),
                            color: selectedCategory === category ? 'white' : 'black',
                        }}
                    >
                        {category}
                    </button>
                ))}
                </div>
                

                {/* Assessments cards */}
                <div className='cards-main'>
                        {categoryFilteredAssessments
                            .slice()
                            .sort(sortByAssessmentName)
                            .map((assessment) => {
                                const categoryColor = getColorByCategory(assessment.examCategory);
                                const darkColor = darkenColor(categoryColor);

                                return (
                                    <div 
                                        className="card" 
                                        key={assessment._id} 
                                        style={{ 
                                            backgroundColor: darkColor,
                                        }}
                                    >
                                        <div className='card-body'>
                                            <h5 className="card-text">{assessment.examName}</h5>
                                            <div className="card-title h5">
                                                {lang.category} {assessment.examCategory}
                                            </div>
                                            <button 
                                                onClick={() => startButton(assessment.examName, assessment.examCategory, assessment.isMSS, assessment._id)} 
                                                type="button" 
                                                className="btn-2"
                                                style={{
                                                    backgroundColor: getColorByCategory(assessment.examCategory),
                                                    border: '1px solid white',
                                                }}
                                            >
                                                {lang.startButton}
                                            </button>
                                        </div>
                                    </div>
                                );
                        })}
                </div>
            </section>
        </div>
    );
};

export default LandingPage;
